import { useEffect, useRef } from 'react';
import { T_LeadsNature } from 'types/app.type';

import { FieldType, FormName } from '@/lib/constants';
import { useAnalytics } from '@/lib/gatrack';

interface Props {
  leadContent: T_LeadsNature;
  dismissAction: (id: string) => void;
}

type ValidatableFormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type TrackFormFieldUpdated = (payload: {
  form_name: string;
  field_type: FieldType;
  field_name: string;
  message: string;
  form_step: string;
  form_section: string;
}) => void;
type TrackErrorViewed = (payload: {
  form_name: string;
  field_type: FieldType;
  field_name: string;
  message: string;
  form_step: string;
  form_section: string;
}) => void;
type TrackedFieldValuesRef = React.MutableRefObject<Record<string, string>>;

const POPUP_ROOT_SELECTOR = "[data-leads-nurture-popup-root='true']";
const POPUP_BODY_SELECTOR = "[data-leads-nurture-popup-body='true']";

const popupFullscreenOverrides = `
  ${POPUP_ROOT_SELECTOR} {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100%;
    padding: 16px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 0.34);
  }

  ${POPUP_BODY_SELECTOR} {
    display: flex !important;
    align-items: center;
    justify-content: center;
    margin: 0;
    width: 100%;
    min-height: calc(100vh - 32px);
    min-height: calc(100dvh - 32px);
    background: transparent !important;
  }

  ${POPUP_BODY_SELECTOR} .sky-modal,
  ${POPUP_BODY_SELECTOR} > :first-child,
  ${POPUP_BODY_SELECTOR} > #success-card,
  ${POPUP_BODY_SELECTOR} > #successCard {
    margin: 0 !important;
    max-width: calc(100vw - 32px) !important;
    max-height: calc(100vh - 32px) !important;
    max-height: calc(100dvh - 32px) !important;
  }

  @media (min-width: 769px) {
    ${POPUP_ROOT_SELECTOR} {
      padding: 24px;
    }

    ${POPUP_BODY_SELECTOR} {
      min-height: calc(100vh - 48px);
      min-height: calc(100dvh - 48px);
    }

    ${POPUP_BODY_SELECTOR} .sky-modal,
    ${POPUP_BODY_SELECTOR} > :first-child,
    ${POPUP_BODY_SELECTOR} > #success-card,
    ${POPUP_BODY_SELECTOR} > #successCard {
      max-width: calc(100vw - 48px) !important;
      max-height: calc(100vh - 48px) !important;
      max-height: calc(100dvh - 48px) !important;
    }
  }

  @media (max-width: 768px) {
    ${POPUP_ROOT_SELECTOR} {
      display: block;
      padding: 0;
      background: radial-gradient(ellipse 59.16% 37.67% at 50% 50%, #00318B 0%, #00013A 100%);
    }

    ${POPUP_BODY_SELECTOR} {
      display: block !important;
      min-height: 100%;
      background: radial-gradient(ellipse 59.16% 37.67% at 50% 50%, #00318B 0%, #00013A 100%) !important;
    }

    ${POPUP_BODY_SELECTOR} .sky-modal,
    ${POPUP_BODY_SELECTOR} > :first-child,
    ${POPUP_BODY_SELECTOR} > #success-card,
    ${POPUP_BODY_SELECTOR} > #successCard {
      width: 100% !important;
      max-width: 100% !important;
      min-height: 100vh !important;
      min-height: 100dvh !important;
      height: auto !important;
      max-height: none !important;
      margin: 0 !important;
      border-radius: 0 !important;
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      overflow-y: auto !important;
      overflow-x: hidden !important;
    }
  }
`;

const normalizeLeadMarkup = (html = '') =>
  html.replace(/src="\/\/images\.ctfassets\.net/g, 'src="https://images.ctfassets.net');

const extractStyleContents = (htmlInput = '') => {
  if (!htmlInput) return [];

  if (typeof DOMParser !== 'undefined') {
    const documentFragment = new DOMParser().parseFromString(htmlInput, 'text/html');

    return Array.from(documentFragment.querySelectorAll('style'))
      .map(styleTag => styleTag.textContent?.trim() ?? '')
      .filter(Boolean);
  }

  const extractedStyles: string[] = [];
  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let styleMatch = styleRegex.exec(htmlInput);

  while (styleMatch) {
    if (styleMatch[1]?.trim()) {
      extractedStyles.push(styleMatch[1].trim());
    }

    styleMatch = styleRegex.exec(htmlInput);
  }

  return extractedStyles;
};

const extractLeadMarkup = (htmlInput = '') => {
  if (!htmlInput) return '';

  if (typeof DOMParser !== 'undefined') {
    const documentFragment = new DOMParser().parseFromString(htmlInput, 'text/html');

    documentFragment
      .querySelectorAll('style, script, meta, title, link, noscript')
      .forEach(node => node.remove());

    return normalizeLeadMarkup(documentFragment.body?.innerHTML?.trim() ?? '');
  }

  return normalizeLeadMarkup(
    htmlInput
      .replace(/<!doctype[^>]*>/gi, '')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<meta[^>]*>/gi, '')
      .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
      .replace(/<link[^>]*>/gi, '')
      .replace(/<noscript[^>]*>[\s\S]*?<\/noscript>/gi, '')
      .replace(/<\/?(html|head|body)[^>]*>/gi, '')
      .trim()
  );
};

const scopePopupStyles = (styleContent: string) =>
  styleContent.replace(/\bhtml\b/g, POPUP_ROOT_SELECTOR).replace(/\bbody\b/g, POPUP_BODY_SELECTOR);

const isValidatableControl = (element: Element): element is ValidatableFormControl =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLSelectElement ||
  element instanceof HTMLTextAreaElement;

const isIgnorableControl = (control: ValidatableFormControl) =>
  control instanceof HTMLInputElement &&
  ['button', 'hidden', 'reset', 'submit'].includes(control.type);

const isCheckboxControl = (control: ValidatableFormControl): control is HTMLInputElement =>
  control instanceof HTMLInputElement && control.type === 'checkbox';

const isSelectControl = (control: ValidatableFormControl): control is HTMLSelectElement =>
  control instanceof HTMLSelectElement;

const isTextLikeControl = (control: ValidatableFormControl) =>
  control instanceof HTMLTextAreaElement ||
  isSelectControl(control) ||
  (control instanceof HTMLInputElement &&
    !['checkbox', 'file', 'hidden', 'radio', 'reset', 'submit'].includes(control.type));

const hasTrimmedEmptyValue = (control: ValidatableFormControl) =>
  isTextLikeControl(control) && control.required && control.value.trim().length === 0;

const getFieldType = (control: ValidatableFormControl): FieldType => {
  if (control instanceof HTMLInputElement) {
    if (control.type === 'checkbox') return FieldType.Checkbox;
    if (control.type === 'radio') return FieldType.Radio;
    if (control.type === 'date') return FieldType.Date;
    if (control.type === 'time') return FieldType.Time;

    return FieldType.Text;
  }

  if (isSelectControl(control)) return FieldType.Select;

  return FieldType.Text;
};

const toFieldLabel = (control: ValidatableFormControl) => {
  const label =
    control.getAttribute('placeholder')?.trim() ||
    control.getAttribute('aria-label')?.trim() ||
    control.getAttribute('data-label')?.trim() ||
    control.name.replace(/[_-]+/g, ' ').trim();

  if (!label) return 'This field';

  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getFieldContainer = (control: ValidatableFormControl) =>
  (control.closest('.field') || control.closest('.sky_nbj-input-group')) as HTMLElement | null;

const getFieldErrorElement = (control: ValidatableFormControl) =>
  (getFieldContainer(control)?.querySelector('.error-message') as HTMLElement | null) ?? null;

const getCheckboxErrorElement = (control: HTMLInputElement, root: HTMLDivElement) => {
  const checkboxRow = control.closest('.checkbox-row');
  const nextSibling = checkboxRow?.nextElementSibling as HTMLElement | null;

  if (nextSibling?.classList.contains('checkbox-error')) {
    return nextSibling;
  }

  return (root.querySelector('#checkboxError') as HTMLElement | null) ?? null;
};

const getValidationMessage = (
  control: ValidatableFormControl,
  errorElement: HTMLElement | null,
  isMissingValue: boolean
) => {
  if (isMissingValue || control.validity.valueMissing) {
    return `${toFieldLabel(control)} is required`;
  }

  if (control instanceof HTMLInputElement && control.type === 'email') {
    return 'Please enter a valid email address';
  }

  if (control.title?.trim()) {
    return control.title.trim();
  }

  if (errorElement?.textContent?.trim()) {
    return errorElement.textContent.trim();
  }

  return 'Please enter a valid value';
};

const getFormControls = (form: HTMLFormElement, selector = 'input, select, textarea') =>
  Array.from(form.querySelectorAll(selector))
    .filter(isValidatableControl)
    .filter(control => !isIgnorableControl(control));

const createHiddenIframe = () => {
  const iframe = document.createElement('iframe');

  iframe.style.display = 'none';
  iframe.name = 'hiddenFrame_' + Date.now();
  document.body.appendChild(iframe);

  return iframe;
};

const cleanupIframe = (iframe: HTMLIFrameElement, delay = 5000) => {
  setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, delay);
};

const getControlTrackingKey = (control: ValidatableFormControl, formSection: string) =>
  `${formSection}:${control.name || control.id || 'unnamed_field'}`;

const getTrackedControlValue = (control: ValidatableFormControl) =>
  isCheckboxControl(control) ? String(control.checked) : control.value;

const trackLeadFieldUpdate = (
  control: ValidatableFormControl,
  formName: string,
  formSection: string,
  trackFormFieldUpdated: TrackFormFieldUpdated,
  trackedFieldValuesRef: TrackedFieldValuesRef
) => {
  const value = getTrackedControlValue(control);
  const trackingKey = getControlTrackingKey(control, formSection);

  if (trackedFieldValuesRef.current[trackingKey] === value) return;

  if (value.trim() === '') return;

  trackFormFieldUpdated({
    form_name: formName,
    field_type: FieldType.Text,
    field_name: control.name || control.id || 'unnamed_field',
    message: value,
    form_step: formName,
    form_section: formSection,
  });

  trackedFieldValuesRef.current[trackingKey] = value;
};

const trackLeadError = ({
  control,
  message,
  formSection,
  formStep,
  trackedErrorsRef,
  trackErrorViewed,
}: {
  control: ValidatableFormControl;
  message: string;
  formSection: string;
  formStep: string;
  trackedErrorsRef: React.MutableRefObject<Record<string, string | undefined>>;
  trackErrorViewed: TrackErrorViewed;
}) => {
  const fieldName = control.name || control.id || 'unnamed_field';
  const trackedErrorKey = `${formSection}:${fieldName}`;

  if (trackedErrorsRef.current[trackedErrorKey] === message) return;

  trackErrorViewed({
    form_name: FormName.leadNurture,
    field_type: getFieldType(control),
    field_name: fieldName,
    message,
    form_step: formStep,
    form_section: formSection,
  });

  trackedErrorsRef.current[trackedErrorKey] = message;
};

const bindControlListeners = (
  controls: ValidatableFormControl[],
  onInteract: (control: ValidatableFormControl) => void,
  onBlur: (control: ValidatableFormControl) => void
) => {
  const inputHandlers: (() => void)[] = [];

  controls.forEach(control => {
    const handleInteract = () => onInteract(control);
    const handleBlur = () => onBlur(control);
    const eventName = isCheckboxControl(control) || isSelectControl(control) ? 'change' : 'input';

    control.addEventListener(eventName, handleInteract);
    control.addEventListener('blur', handleBlur);

    inputHandlers.push(() => {
      control.removeEventListener(eventName, handleInteract);
      control.removeEventListener('blur', handleBlur);
    });
  });

  return inputHandlers;
};

const validateCheckboxControl = (control: HTMLInputElement, checkboxError: HTMLElement | null) => {
  const isValid = !control.required || control.checked;

  if (!isValid) {
    control.classList.add('error');
  } else {
    control.classList.remove('error');
  }

  if (checkboxError) {
    checkboxError.style.display = isValid ? 'none' : 'block';
  } else if (!isValid) {
    control.reportValidity();
  }

  return isValid;
};

const clearCheckboxControlError = (
  control: HTMLInputElement,
  checkboxError: HTMLElement | null
) => {
  if (!control.checked) return;

  control.classList.remove('error');

  if (checkboxError) {
    checkboxError.style.display = 'none';
  }
};

export const LeadsNurture = ({ leadContent, dismissAction }: Props) => {
  const {
    trackPopupViewed,
    trackCtaClicked,
    trackErrorViewed,
    trackFormFieldUpdated,
    trackFormSubmitted,
  } = useAnalytics();

  const isBottom = leadContent.alignment === 'Bottom';
  const isDiscover = leadContent.alignment === 'Discover';
  const isPopup = !isBottom && !isDiscover;
  const cleanHtml = extractLeadMarkup(leadContent.htmlInput);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackedPopupViewRef = useRef<string | null>(null);
  const trackedErrorsRef = useRef<Record<string, string | undefined>>({});
  const trackedFieldValuesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!isPopup || !containerRef.current) return;

    const titleEl = containerRef.current.querySelector('.title') as HTMLElement | null;
    const popupName = titleEl?.textContent?.trim() || leadContent.id || 'Leads Nurture Popup';
    const popupViewKey = `${leadContent.id || 'unknown'}::${leadContent.alignment}::${popupName}`;

    if (trackedPopupViewRef.current === popupViewKey) return;

    trackedPopupViewRef.current = popupViewKey;

    trackPopupViewed({
      name: popupName,
      component: 'POPUP',
      context: `Leads Nurture / ${leadContent.alignment}`,
    });
  }, [isPopup, leadContent.id, leadContent.alignment, trackPopupViewed]);

  useEffect(() => {
    if (!leadContent.htmlInput) return;

    const styleTags: HTMLStyleElement[] = [];
    const extractedStyles = extractStyleContents(leadContent.htmlInput);

    extractedStyles.forEach(styleContent => {
      const styleTag = document.createElement('style');

      styleTag.innerHTML = isPopup ? scopePopupStyles(styleContent) : styleContent;
      document.head.appendChild(styleTag);
      styleTags.push(styleTag);
    });

    if (isPopup) {
      const popupStyleTag = document.createElement('style');

      popupStyleTag.innerHTML = popupFullscreenOverrides;
      document.head.appendChild(popupStyleTag);
      styleTags.push(popupStyleTag);
    }

    if (styleTags.length === 0) return;

    return () => {
      styleTags.forEach(styleTag => {
        if (styleTag.parentNode) {
          styleTag.parentNode.removeChild(styleTag);
        }
      });
    };
  }, [leadContent.htmlInput, isPopup]);

  useEffect(() => {
    if (!isPopup || !containerRef.current) return;

    const popupRoot = containerRef.current;
    const formEl = popupRoot.querySelector('form') as HTMLFormElement | null;
    const successCard = popupRoot.querySelector('#success-card') as HTMLDivElement | null;
    const title = popupRoot.querySelector('.title') as HTMLElement | null;
    const subtitle = popupRoot.querySelector('.subtitle') as HTMLElement | null;
    const closeBtn = popupRoot.querySelector('#close-modalIcon') as HTMLButtonElement | null;

    if (!formEl) return;

    const formControls = getFormControls(formEl);
    const formName = formEl.name || formEl.id || 'leads_nurture_popup_form';

    formControls.forEach(control => {
      trackedFieldValuesRef.current[getControlTrackingKey(control, 'popup_details')] =
        getTrackedControlValue(control);
    });

    const hideFieldError = (control: ValidatableFormControl) => {
      const fieldContainer = getFieldContainer(control);
      const errorElement = getFieldErrorElement(control);

      if (fieldContainer) {
        fieldContainer.classList.remove('error');
      }

      if (errorElement) {
        errorElement.style.display = 'none';
      }

      delete trackedErrorsRef.current[
        `popup_details:${control.name || control.id || 'unnamed_field'}`
      ];
    };

    const validateControl = (control: ValidatableFormControl) => {
      if (control.disabled) return true;

      if (isCheckboxControl(control)) {
        const checkboxError = getCheckboxErrorElement(control, popupRoot);
        const isValid = validateCheckboxControl(control, checkboxError);

        if (!isValid) {
          trackLeadError({
            control,
            message: 'This field is required',
            formSection: 'popup_details',
            formStep: formName,
            trackedErrorsRef,
            trackErrorViewed,
          });
        } else {
          delete trackedErrorsRef.current[
            `popup_details:${control.name || control.id || 'unnamed_field'}`
          ];
        }

        return isValid;
      }

      const fieldContainer = getFieldContainer(control);
      const errorElement = getFieldErrorElement(control);
      const isMissingValue = hasTrimmedEmptyValue(control);
      const isValid = !isMissingValue && control.checkValidity();

      if (!isValid) {
        const validationMessage = getValidationMessage(control, errorElement, isMissingValue);

        if (fieldContainer) {
          fieldContainer.classList.add('error');
        }

        if (errorElement) {
          errorElement.textContent = validationMessage;
          errorElement.style.display = 'block';
        } else {
          control.reportValidity();
        }

        trackLeadError({
          control,
          message: validationMessage,
          formSection: 'popup_details',
          formStep: formName,
          trackedErrorsRef,
          trackErrorViewed,
        });
      } else {
        hideFieldError(control);
      }

      return isValid;
    };

    const inputHandlers = bindControlListeners(
      formControls,
      control => {
        if (isCheckboxControl(control)) {
          clearCheckboxControlError(control, getCheckboxErrorElement(control, popupRoot));
          delete trackedErrorsRef.current[
            `popup_details:${control.name || control.id || 'unnamed_field'}`
          ];
          return;
        }

        hideFieldError(control);
      },
      control =>
        trackLeadFieldUpdate(
          control,
          formName,
          'popup_details',
          trackFormFieldUpdated,
          trackedFieldValuesRef
        )
    );

    const handleSubmit = (event: Event) => {
      event.preventDefault();

      let formIsValid = true;

      formControls.forEach(control => {
        if (!validateControl(control)) {
          formIsValid = false;
        }
      });

      if (!formIsValid) return;

      trackFormSubmitted(FormName.leadNurture);

      const iframe = createHiddenIframe();

      formEl.target = iframe.name;

      formEl.submit();
      formEl.style.display = 'none';

      if (title) title.style.display = 'none';
      if (subtitle) subtitle.style.display = 'none';
      if (successCard) successCard.style.display = 'block';

      cleanupIframe(iframe);
    };

    const handleClose = () => {
      trackCtaClicked({
        name: 'Close Popup',
        component: 'POPUP',
        context: `Leads Nurture / Dismissed ${leadContent.id}`,
      });
      if (isPopup) {
        dismissAction(leadContent.id);
      }
    };
    formEl.addEventListener('submit', handleSubmit);

    if (closeBtn) {
      closeBtn.addEventListener('click', handleClose);
    }

    return () => {
      formEl.removeEventListener('submit', handleSubmit);

      if (closeBtn) {
        closeBtn.removeEventListener('click', handleClose);
      }

      inputHandlers.forEach(cleanup => cleanup());
    };
  }, [
    dismissAction,
    isPopup,
    leadContent,
    trackCtaClicked,
    trackErrorViewed,
    trackFormFieldUpdated,
    trackFormSubmitted,
  ]);

  useEffect(() => {
    if (!isPopup) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isPopup]);

  useEffect(() => {
    if (!isBottom || !containerRef.current) return;

    const form = containerRef.current.querySelector('#form') as HTMLFormElement | null;
    const formCard = containerRef.current.querySelector('#formCard') as HTMLElement | null;
    const successCard = containerRef.current.querySelector('#successCard') as HTMLElement | null;
    const checkboxError = containerRef.current.querySelector(
      '#checkboxError'
    ) as HTMLElement | null;

    if (!form) return;

    const requiredControls = getFormControls(
      form,
      'input[required], select[required], textarea[required]'
    );
    const formName = form.name || form.id || 'leads_nurture_bottom_form';

    requiredControls.forEach(control => {
      trackedFieldValuesRef.current[getControlTrackingKey(control, 'bottom_form_details')] =
        getTrackedControlValue(control);
    });

    const validateField = (control: ValidatableFormControl) => {
      if (control.disabled) return true;

      if (isCheckboxControl(control)) {
        const isValid = validateCheckboxControl(control, checkboxError);

        if (!isValid) {
          trackLeadError({
            control,
            message: 'This field is required',
            formSection: 'bottom_form_details',
            formStep: formName,
            trackedErrorsRef,
            trackErrorViewed,
          });
        } else {
          delete trackedErrorsRef.current[
            `bottom_form_details:${control.name || control.id || 'unnamed_field'}`
          ];
        }

        return isValid;
      }

      const group = control.closest('.sky_nbj-input-group') as HTMLElement | null;
      const isMissingValue = hasTrimmedEmptyValue(control);
      const isValid = !isMissingValue && control.checkValidity();

      if (!isValid) {
        if (group) group.classList.add('error');
        if (!group) control.reportValidity();

        trackLeadError({
          control,
          message: getValidationMessage(control, null, isMissingValue),
          formSection: 'bottom_form_details',
          formStep: formName,
          trackedErrorsRef,
          trackErrorViewed,
        });
      } else {
        if (group) group.classList.remove('error');
        delete trackedErrorsRef.current[
          `bottom_form_details:${control.name || control.id || 'unnamed_field'}`
        ];
      }

      return isValid;
    };

    const inputHandlers = bindControlListeners(
      requiredControls,
      control => {
        if (isCheckboxControl(control)) {
          clearCheckboxControlError(control, checkboxError);
          return;
        }

        validateField(control);
      },
      control =>
        trackLeadFieldUpdate(
          control,
          formName,
          'bottom_form_details',
          trackFormFieldUpdated,
          trackedFieldValuesRef
        )
    );

    const handleSubmit = (event: Event) => {
      event.preventDefault();

      let isValid = true;

      requiredControls.forEach(control => {
        if (!validateField(control)) {
          isValid = false;
        }
      });

      if (!isValid) return;

      trackFormSubmitted(FormName.leadNurture);

      const iframe = createHiddenIframe();

      form.target = iframe.name;
      form.submit();

      if (formCard) formCard.style.display = 'none';
      if (successCard) successCard.style.display = 'block';

      setTimeout(() => {
        form.reset();

        requiredControls.forEach(control => {
          const group = control.closest('.sky_nbj-input-group') as HTMLElement | null;

          if (group) group.classList.remove('error');
        });

        if (checkboxError) checkboxError.style.display = 'none';
      }, 500);

      cleanupIframe(iframe);
    };

    form.addEventListener('submit', handleSubmit);

    return () => {
      form.removeEventListener('submit', handleSubmit);
      inputHandlers.forEach(cleanup => cleanup());
    };
  }, [isBottom, trackErrorViewed, trackFormFieldUpdated, trackFormSubmitted]);

  return (
    <>
      {isBottom || isDiscover ? (
        <div ref={containerRef}>
          <div dangerouslySetInnerHTML={{ __html: cleanHtml || '' }} />
        </div>
      ) : (
        <div
          ref={containerRef}
          data-leads-nurture-popup-root="true"
          className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden"
          style={{ minHeight: '100vh', height: '100dvh', backdropFilter: 'blur(4px)' }}
        >
          <div
            data-leads-nurture-popup-body="true"
            className="min-h-full w-full"
            dangerouslySetInnerHTML={{ __html: cleanHtml || '' }}
          />
        </div>
      )}
    </>
  );
};
